import { useCallback, useEffect, useState } from 'react';
import { employeeReadinessGateway } from '../employee-readiness';
import type { EmployeeSetupReadiness } from '../employee-readiness';
import { listAllEmployees, peopleGateway } from '../people';
import type { EmployeeResponse } from '../people/types';
import { classifyFailure, type FailureReason } from './safeErrors';

/** The readiness endpoint accepts 1–100 employee ids per request (EPN-94 contract). */
const READINESS_BATCH_SIZE = 100;

export type EmployeesLoad =
  | { kind: 'loading' }
  | { kind: 'loaded'; employees: EmployeeResponse[] }
  | { kind: 'error'; reason: FailureReason };

export type ReadinessLoad =
  | { kind: 'loading' }
  | { kind: 'loaded'; byId: Record<string, EmployeeSetupReadiness> }
  | { kind: 'error'; reason: FailureReason };

export interface PayEmployeesData {
  employees: EmployeesLoad;
  readiness: ReadinessLoad;
  retryEmployees: () => void;
  /** Re-reads readiness from the server (e.g. after an administrator fixed something). */
  recheckReadiness: () => void;
}

async function queryAllReadiness(ids: string[]): Promise<Record<string, EmployeeSetupReadiness>> {
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += READINESS_BATCH_SIZE) batches.push(ids.slice(i, i + READINESS_BATCH_SIZE));
  const responses = await Promise.all(batches.map((batch) => employeeReadinessGateway.queryReadiness(batch)));
  const byId: Record<string, EmployeeSetupReadiness> = {};
  for (const response of responses) {
    for (const result of response.results) byId[result.employeeId] = result;
  }
  return byId;
}

/**
 * Loads exactly two things, both read-only: the employee list (existing People gateway) and the authoritative
 * EPN-94 readiness projection for those employees (existing EmployeeReadinessGateway). It owns no other
 * data and calls nothing that creates, calculates, approves or pays anything.
 *
 * "Loading" is derived (the settled result belongs to a different request key) so no state is set
 * synchronously inside an effect — the same pattern as useEmployeeReadiness.
 */
export function usePayEmployeesData(): PayEmployeesData {
  const [employeesNonce, setEmployeesNonce] = useState(0);
  const [employeesSettled, setEmployeesSettled] = useState<{ nonce: number; outcome: EmployeesLoad } | null>(null);

  useEffect(() => {
    let cancelled = false;
    listAllEmployees(peopleGateway)
      .then((employees) => {
        if (!cancelled) setEmployeesSettled({ nonce: employeesNonce, outcome: { kind: 'loaded', employees } });
      })
      .catch((err: unknown) => {
        if (!cancelled) setEmployeesSettled({ nonce: employeesNonce, outcome: { kind: 'error', reason: classifyFailure(err) } });
      });
    return () => {
      cancelled = true;
    };
  }, [employeesNonce]);

  const employees: EmployeesLoad = employeesSettled?.nonce === employeesNonce ? employeesSettled.outcome : { kind: 'loading' };

  const [readinessNonce, setReadinessNonce] = useState(0);
  const ids = employees.kind === 'loaded' ? employees.employees.map((e) => e.id) : null;
  const readinessKey = ids === null ? null : `${readinessNonce}#${ids.join('|')}`;
  const [readinessSettled, setReadinessSettled] = useState<{ key: string; outcome: ReadinessLoad } | null>(null);

  useEffect(() => {
    if (readinessKey === null || ids === null || ids.length === 0) return;
    let cancelled = false;
    queryAllReadiness(ids)
      .then((byId) => {
        if (!cancelled) setReadinessSettled({ key: readinessKey, outcome: { kind: 'loaded', byId } });
      })
      .catch((err: unknown) => {
        if (!cancelled) setReadinessSettled({ key: readinessKey, outcome: { kind: 'error', reason: classifyFailure(err) } });
      });
    return () => {
      cancelled = true;
    };
    // `ids` is fully determined by `readinessKey`; depending on the key avoids re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readinessKey]);

  let readiness: ReadinessLoad;
  if (ids !== null && ids.length === 0) readiness = { kind: 'loaded', byId: {} };
  else readiness = readinessSettled !== null && readinessSettled.key === readinessKey ? readinessSettled.outcome : { kind: 'loading' };

  const retryEmployees = useCallback(() => setEmployeesNonce((n) => n + 1), []);
  const recheckReadiness = useCallback(() => setReadinessNonce((n) => n + 1), []);

  return { employees, readiness, retryEmployees, recheckReadiness };
}
